import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { cn } from '../../lib/cn';
import { DynamicIsland } from '../ui';
import GrammarObservationTags from '../grading/GrammarObservationTags';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function ConfidenceBadge({ confidence }) {
  if (confidence == null) return null;
  const pct = Math.round(confidence * 100);
  const color = confidence < 0.5
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    : confidence < 0.85
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';

  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium', color)}>
      {pct}% confidence
    </span>
  );
}

export default function SubmissionExpand({ student, rowData, questions, activityId, focusedQuestionId, grammarConcepts = [], onClose, onSaved }) {
  const cellData = rowData?.responses?.[focusedQuestionId] || {};
  const question = questions.find(q => q.id === focusedQuestionId);

  if (!question) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Question not found.
        <button onClick={onClose} className="ml-4 text-sm text-blue-600 hover:underline">Close</button>
      </div>
    );
  }

  const hasResponse = !!cellData.response_id;

  return (
    <div>
      {/* Header bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {student.full_name} — Q{question.position}
          </span>
          {hasResponse ? (
            <>
              <ConfidenceBadge confidence={cellData.transcription_confidence} />
              {cellData.content_type === 'handwritten' && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  Handwritten
                </span>
              )}
            </>
          ) : (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
              No response — enter manually
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content: scan + form side by side */}
      <div className="flex divide-x divide-gray-200 dark:divide-gray-700" style={{ height: '70vh' }}>
        {/* Left: PDF / scan viewer — takes 3/5 width */}
        <ScanViewer
          scanUrl={cellData.scan_url}
          sourceDocumentUrl={cellData.source_document_url || student.source_document_url}
          sourcePage={question.source_page}
        />

        {/* Right: review form — takes 2/5 width */}
        <ReviewForm
          key={cellData.response_id || `new-${focusedQuestionId}`}
          cellData={cellData}
          question={question}
          activityId={activityId}
          studentId={student.id}
          grammarConcepts={grammarConcepts}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

function ScanViewer({ scanUrl, sourceDocumentUrl, sourcePage }) {
  const url = scanUrl || sourceDocumentUrl;
  const isPdf = url && (url.includes('.pdf') || url.includes('content_type=application%2Fpdf') || url.includes('content-type=application/pdf'));

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(sourcePage || 1);
  const [scale, setScale] = useState(1.0);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  // Navigate to sourcePage when it changes (e.g. switching questions)
  useEffect(() => {
    if (sourcePage) setPageNumber(sourcePage);
  }, [sourcePage]);

  if (!url) {
    return (
      <div className="w-3/5 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm italic">
        No scan image available
      </div>
    );
  }

  if (!isPdf) {
    return (
      <div className="w-3/5 bg-gray-50 dark:bg-gray-900 p-4 overflow-auto">
        <img
          src={url}
          alt="Handwriting scan"
          className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-700"
        />
      </div>
    );
  }

  return (
    <div className="w-3/5 flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Compact toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
            {pageNumber} / {numPages || '…'}
          </span>
          <button onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))} disabled={pageNumber >= (numPages || 1)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))} disabled={scale <= 0.5}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(2.0, s + 0.25))} disabled={scale >= 2.0}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
          <button onClick={() => { const w = window.open(url, '_blank'); if (w) w.onload = () => w.print(); }}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            title="Print">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          <a href={url} download className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400" title="Download">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
        </div>
      </div>

      {/* PDF content */}
      <div className="flex-1 overflow-auto flex justify-center p-4">
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex items-center justify-center h-full">
              <span className="text-sm text-gray-400 dark:text-gray-500">Loading PDF…</span>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            className="shadow-lg"
          />
        </Document>
      </div>
    </div>
  );
}

function ReviewForm({ cellData, question, activityId, studentId, grammarConcepts = [], onSaved }) {
  const [content, setContent] = useState(cellData.content || '');
  const [selectedIndex, setSelectedIndex] = useState(cellData.selected_index ?? null);
  const [correct, setCorrect] = useState(cellData.correct);
  const [score, setScore] = useState(cellData.score ?? '');
  const [contentScore, setContentScore] = useState(cellData.content_score != null ? parseFloat(cellData.content_score) : null);
  const [grammarScore, setGrammarScore] = useState(cellData.grammar_score != null ? parseFloat(cellData.grammar_score) : null);
  const [transcriptionVerified, setTranscriptionVerified] = useState(cellData.transcription_verified || false);
  const [grammarObservations, setGrammarObservations] = useState(cellData.grammar_observations || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [responseId, setResponseId] = useState(cellData.response_id || null);

  const isMCQ = question.question_type === 'mcq' || question.question_type === 'multiple_choice';
  const isShortAnswer = question.question_type === 'short_answer' || question.question_type === 'essay' || question.question_type === 'fill_blank';
  const options = cellData.question_options || question.options || [];
  const isHandwritten = cellData.content_type === 'handwritten';
  const isTyped = cellData.content_type === 'typed';
  const highConfidence = (cellData.transcription_confidence ?? 0) >= 0.85;

  // Transcription display: plain text if verified/high-confidence/typed, else editable
  const [editMode, setEditMode] = useState(
    isShortAnswer && isHandwritten && !transcriptionVerified && !highConfidence
  );

  // Dirty state tracking
  const initialState = useRef({
    content: cellData.content || '',
    selectedIndex: cellData.selected_index ?? null,
    contentScore: cellData.content_score ?? null,
    grammarScore: cellData.grammar_score ?? null,
    transcriptionVerified: cellData.transcription_verified || false,
    correct: cellData.correct,
    score: cellData.score ?? '',
  });

  // Response is verified but hasn't been evaluated yet — save should trigger evaluation
  const needsEvaluation = isShortAnswer && transcriptionVerified &&
    !(cellData.grammar_evaluated && grammarScore != null) &&
    content.trim() !== '';

  const isDirty = useMemo(() => {
    if (isMCQ) {
      return selectedIndex !== initialState.current.selectedIndex;
    }
    return content !== initialState.current.content ||
      contentScore !== initialState.current.contentScore ||
      grammarScore !== initialState.current.grammarScore ||
      transcriptionVerified !== initialState.current.transcriptionVerified ||
      correct !== initialState.current.correct ||
      String(score) !== String(initialState.current.score) ||
      needsEvaluation;
  }, [content, selectedIndex, contentScore, grammarScore, transcriptionVerified, correct, score, isMCQ, needsEvaluation]);

  // Derived total score
  const derivedTotal = useMemo(() => {
    if (contentScore != null && grammarScore != null) {
      return Math.round((contentScore + grammarScore) / 2 * 100);
    }
    return null;
  }, [contentScore, grammarScore]);

  // Will this save trigger an AI grammar evaluation?
  const willEvaluate = isShortAnswer && content.trim() !== '' &&
    !(cellData.grammar_evaluated && grammarScore != null) &&
    (isTyped || transcriptionVerified);

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

  // Immediately persist transcription_verified so it survives question switches
  const handleVerificationChange = (checked) => {
    setTranscriptionVerified(checked);
    if (checked) setEditMode(false);
    if (!responseId) return;

    // Optimistically update parent grid BEFORE the PATCH completes
    // so the state survives if the user switches questions immediately
    onSaved(question.id, { ...cellData, transcription_verified: checked });
    initialState.current = { ...initialState.current, transcriptionVerified: checked };

    fetch(`/teacher/submissions/${responseId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
        'Accept': 'application/json',
      },
      body: JSON.stringify({ response: { transcription_verified: checked } }),
    }).catch(() => {
      // Revert on failure
      setTranscriptionVerified(!checked);
      if (!checked) setEditMode(true);
      onSaved(question.id, { ...cellData, transcription_verified: !checked });
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    const body = { response: {} };
    if (isMCQ) {
      body.response.selected_index = selectedIndex;
      if (Array.isArray(options)) {
        const correctIdx = options.findIndex(opt => opt.correct === true);
        body.response.correct = selectedIndex != null ? selectedIndex === correctIdx : null;
      }
    } else {
      body.response.content = content;
      body.response.correct = correct;
      body.response.score = score === '' ? null : parseInt(score, 10);
      if (contentScore !== null) {
        body.response.content_score = contentScore;
      }
      if (grammarScore !== null) {
        body.response.grammar_score = grammarScore;
      }
      if (isHandwritten) {
        body.response.transcription_verified = transcriptionVerified;
      }
    }

    const isCreate = !responseId;
    if (isCreate) {
      body.response.question_id = question.id;
      body.response.student_id = studentId;
    }

    try {
      const url = isCreate
        ? `/teacher/activities/${activityId}/submissions`
        : `/teacher/submissions/${responseId}`;
      const res = await fetch(url, {
        method: isCreate ? 'POST' : 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.errors?.join(', ') || 'Save failed');
      }

      const data = await res.json();
      if (isCreate && data.response?.id) {
        setResponseId(data.response.id);
      }
      if (data.response) {
        if (data.response.grammar_observations) {
          setGrammarObservations(data.response.grammar_observations);
        }
        if (data.response.content_score != null) {
          setContentScore(parseFloat(data.response.content_score));
        }
        if (data.response.grammar_score != null) {
          setGrammarScore(parseFloat(data.response.grammar_score));
        }
        if (data.response.score != null) {
          setScore(data.response.score);
        }
        if (data.response.correct != null) {
          setCorrect(data.response.correct);
        }
      }
      // Update initial state so dirty tracking resets
      initialState.current = {
        content,
        selectedIndex,
        contentScore: data.response?.content_score ?? contentScore,
        grammarScore: data.response?.grammar_score ?? grammarScore,
        transcriptionVerified,
        correct: data.response?.correct ?? correct,
        score: data.response?.score ?? score,
      };
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved(question.id, data.response);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveObservation = async (observationId) => {
    try {
      const res = await fetch(`/teacher/submissions/${responseId}/confirm_grammar`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'Accept': 'application/json',
        },
        body: JSON.stringify({ observations: [{ id: observationId, action: 'reject' }] }),
      });
      if (!res.ok) throw new Error('Failed to remove');
      const data = await res.json();
      setGrammarObservations(data.grammar_observations);
      if (data.response) onSaved(question.id, data.response);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAddObservation = async (obsData) => {
    try {
      const res = await fetch(`/teacher/submissions/${responseId}/add_grammar_observation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'Accept': 'application/json',
        },
        body: JSON.stringify(obsData),
      });
      if (!res.ok) throw new Error('Failed to add observation');
      const data = await res.json();
      setGrammarObservations(prev => [...prev, data.observation]);
    } catch (e) {
      setError(e.message);
    }
  };

  const EVALUATION_STEPS = [
    'Saving response…',
    'Analyzing content accuracy…',
    'Evaluating grammar…',
    'Scoring observations…',
  ];

  return (
    <div className="w-2/5 p-5 space-y-4 overflow-y-auto">
      <DynamicIsland
        isActive={saving && willEvaluate}
        steps={EVALUATION_STEPS}
        stepDuration={2000}
      />

      {/* Question prompt — full text */}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Question {question.position}
          </label>
          {activityId && (
            <a
              href={`/teacher/activities/${activityId}/edit#q-${question.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              Edit question
            </a>
          )}
        </div>
        <p className="text-sm text-gray-900 dark:text-white mt-1 leading-relaxed">
          {question.prompt || question.prompt_preview}
        </p>
      </div>

      {/* === MCQ FLOW (unchanged) === */}
      {isMCQ ? (
        <>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
              Selected Answer
            </label>
            {Array.isArray(options) ? (
              <div className="space-y-2">
                {options.map((opt, idx) => {
                  const letter = String.fromCharCode(65 + idx);
                  const isCorrectOption = opt.correct === true;
                  const isSelected = selectedIndex === idx;
                  return (
                    <button
                      type="button"
                      key={idx}
                      onClick={() => setSelectedIndex(isSelected ? null : idx)}
                      className={cn(
                        'flex items-start gap-3 p-2.5 rounded-lg border-2 cursor-pointer transition-all w-full text-left',
                        isSelected && isCorrectOption && 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700',
                        isSelected && !isCorrectOption && 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700',
                        !isSelected && 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      )}
                    >
                      <span className={cn(
                        'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center',
                        isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-400'
                      )}>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white">
                        <span className="font-medium">{letter}.</span>{' '}
                        {opt.text}
                        {isCorrectOption && (
                          <span className="ml-2 text-green-600 dark:text-green-400 text-xs font-medium">(correct)</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(options).map(([key, text]) => (
                  <label
                    key={key}
                    className={cn(
                      'flex items-start gap-3 p-2.5 rounded-lg border-2 cursor-pointer transition-all',
                      content?.toUpperCase() === key.toUpperCase()
                        ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                  >
                    <input
                      type="radio"
                      name="mcq_option"
                      checked={content?.toUpperCase() === key.toUpperCase()}
                      onChange={() => setContent(key)}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">
                      <span className="font-medium">{key}.</span> {text}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* === STEP 1: TRANSCRIPTION === */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Transcription
              </label>
              {isShortAnswer && !editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Edit transcription"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>

            {editMode ? (
              <>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {isHandwritten && (
                  <label className="flex items-center gap-2 cursor-pointer mt-1.5">
                    <input
                      type="checkbox"
                      checked={transcriptionVerified}
                      onChange={(e) => handleVerificationChange(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      Transcription verified
                    </span>
                    {!transcriptionVerified && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        (required for grammar evaluation)
                      </span>
                    )}
                  </label>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                {content || <span className="italic text-gray-400">No response</span>}
              </p>
            )}
          </div>

          {/* === STEP 2: CONTENT ACCURACY === */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
              Content Accuracy
            </label>
            <div className="flex items-center gap-2">
              {[0, 0.5, 1].map(val => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setContentScore(contentScore === val ? null : val)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-xs font-medium border transition-colors',
                    contentScore === val
                      ? val === 0
                        ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                        : val === 0.5
                          ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                          : 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
                  )}
                >
                  {val}
                </button>
              ))}
              <input
                type="number"
                min="0"
                max="1"
                step="0.25"
                value={contentScore ?? ''}
                onChange={(e) => setContentScore(e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="custom"
                className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-xs"
              />
            </div>
            {cellData.content_feedback && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 italic">
                {cellData.content_feedback}
              </p>
            )}
          </div>

          {/* === STEP 3: GRAMMAR === */}
          {responseId && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                Grammar
              </label>
              <GrammarObservationTags
                observations={grammarObservations}
                onRemove={handleRemoveObservation}
                onAdd={handleAddObservation}
                conceptsList={grammarConcepts}
              />
              {/* Grammar score */}
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Grammar score:</span>
                  {[0, 0.5, 1].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setGrammarScore(grammarScore === val ? null : val)}
                      className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium border transition-colors',
                        grammarScore === val
                          ? val === 0
                            ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                            : val === 0.5
                              ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                              : 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
                      )}
                    >
                      {val}
                    </button>
                  ))}
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={grammarScore ?? ''}
                    onChange={(e) => setGrammarScore(e.target.value === '' ? null : parseFloat(e.target.value))}
                    placeholder="—"
                    className="w-14 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-0.5 text-xs"
                  />
                </div>
                {cellData.grammar_feedback && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    {cellData.grammar_feedback}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* === STEP 4: TOTAL SCORE === */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
              Total Score
            </label>
            {derivedTotal != null ? (
              <div className="flex items-center gap-3">
                <span className={cn(
                  'text-lg font-bold',
                  derivedTotal < 50 ? 'text-red-600 dark:text-red-400' :
                  derivedTotal < 80 ? 'text-amber-600 dark:text-amber-400' :
                  'text-green-600 dark:text-green-400'
                )}>
                  {derivedTotal}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  = ({contentScore ?? '?'} content + {grammarScore ?? '?'} grammar) / 2 &times; 100
                </span>
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Awaiting evaluation — content and grammar scores needed
              </p>
            )}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 dark:text-gray-400">Override:</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder={derivedTotal != null ? String(derivedTotal) : '—'}
                  className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-xs"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={correct || false}
                  onChange={(e) => setCorrect(e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Correct</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !isDirty}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            saving || !isDirty
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          )}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && (
          <span className="text-sm text-green-600 dark:text-green-400">Saved!</span>
        )}
        {error && (
          <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        )}
      </div>
    </div>
  );
}
